import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import axios from "axios"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import _ from "lodash"
import Input from "components/forms/input"
import ErrorMessage from "components/forms/error-message"
import SelectSearchable from "components/forms/select-searchable"
import { useAuth } from "contexts/auth-context"
import { useResultSet } from "repositories/db-repository"
import useBranchRepositories from "repositories/branch"
import { getBranchList, getCurrentEntity, getParameter } from "helpers/general-helper"
import { insertHoOrder } from "helpers/transaction-helper"
import { defaultUomForEntity, loadMasterUomFactors, masterUomFactor, normalizeUomCode } from "helpers/uom-helper"
import CustomerPickerModal from "pages/customer-notes/customer-picker-modal"
import ProductPickerModal from "pages/purchase/product-picker-modal"


function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function moneyFmt(v) {
  const n = num(v)
  return Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR" }).format(n)
}

// 210726 (permintaan user): qty lusin/pasang pecahan SELALU tampil 2 desimal
// ("0,50", "1,17" — bukan "0,5"); bilangan bulat tetap tanpa koma ("2").
function qtyFmt(v) {
  const n = num(v)
  if (Number.isInteger(n)) return String(n)
  return n.toLocaleString("id-ID", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Varian utk <input type="number"> (titik desimal wajib): pecahan → "0.50", bulat → "2".
// Dipakai onBlur supaya tampilan input konsisten 2 desimal dgn print.
function qtyInputStr(v) {
  const n = Math.round(num(v) * 100) / 100
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}

// 200726 (PCS/LUSIN): baris LUSIN — qty dalam lusin & harga per lusin
// (konvensi POS; server memotong stok qty×12 dari suffix "(LUSIN)").
// 210726: + PASANG (2 pcs).
// 290726: opsi satuan kini dari MASTER satuan (LSN/PSG/GRS/…) seperti menu
// Tambah Pembelian; daftar di bawah hanya fallback saat master belum termuat.
const UOM_OPTIONS = ["PCS", "LUSIN", "PASANG"]
const uomFactor = masterUomFactor

// 290726: baris ber-faktor 12 memakai harga LUSIN (dozen_store_price) bila ada.
// Dicek lewat faktor, bukan nama, supaya kode master "LSN"/"DGR" ikut tertangani.
const isDozenUom = (u) => {
  const uu = String(u || "").toUpperCase()
  return uu === "LUSIN" || uu === "LSN" || uomFactor(u) === 12
}

// Potongan diskon PRODUK per unit — mirror rumus server (createHoOrder):
// persen dari harga, di-cap max_discount_amount bila > 0; amount-only = potongan tetap.
// Nominal diskon tersimpan per PCS → diskalakan ×12 utk baris LUSIN (mirror server).
function lineProductDiscount(it) {
  const price = num(it.price)
  const f = uomFactor(it.uom)
  const pct = num(it.discount_percentage)
  const amt = num(it.max_discount_amount) * f
  let cut = 0
  if (pct > 0) {
    cut = (price * pct) / 100
    if (amt > 0) cut = Math.min(cut, amt)
  } else if (amt > 0) {
    cut = amt
  }
  return Math.min(cut, price) * num(it.qty)
}

function pickSellPrice(p) {
  const candidates = [p?.price, p?.store_price, p?.maximum_retail_price]
  for (const c of candidates) {
    const n = Number(c)
    if (Number.isFinite(n) && n > 0) return n
  }
  return 0
}

function pickProductLabel(p) {
  return p?.product_unit_long_name || p?.product_unit || p?.sku || p?.product_model || ""
}

// 210726 (permintaan user): outlet terpilih TIDAK punya harga produk -> ambil harga
// HO Atalla (outlet pusat; param REMAX_HQ_OUTLET_ID, default outlet 1). Mengembalikan
// { price, dozen } atau null bila pusat pun tak punya.
const fetchHqFallbackPrice = async (productUnitId) => {
  try {
    const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
    const hq = getParameter("REMAX_HQ_OUTLET_ID") || "1"
    const res = await axios.get(`${base}/ps_list`, {
      // 290726: tanpa discount_scope -> basis store_price/both, konsisten dgn daftar produk.
      params: { outlet_id: hq, product_unit_id: productUnitId, per_page: 1 },
    })
    const row = res?.data?.data?.[0]
    if (!row) return null
    const price = num(row.price) > 0 ? num(row.price) : num(row.maximum_retail_price)
    return { price, dozen: num(row.dozen_store_price) }
  } catch {
    return null
  }
}

export default function Form() {
  const navigate = useNavigate()
  const { currentBranch, user, canFilterAllOutlets } = useAuth()

  // 230726 (permintaan user): mode EDIT untuk order REJECTED — form yang sama
  // di-prefill dari GET /orders/{id}, submit ke orders/{id}/update-ho (order
  // kembali SUBMITTED / antre approve lagi).
  const { id: editId } = useParams()
  const isEdit = Boolean(editId)

  const canPickOutlet = canFilterAllOutlets()
  const { data: outletData } = useBranchRepositories({ paginate: false })
  const outletOptions = useMemo(() => {
    const rows = Array.isArray(outletData) ? outletData : outletData?.data ?? []
    // 230726 (rule entity): order HO hanya bisa dibuat utk outlet ENTITAS user.
    // Outlet tanpa data entity (pra-migrasi) tidak difilter agar aman.
    const entity = getCurrentEntity()
    return entity ? rows.filter((b) => !b?.entity || b.entity === entity) : rows
  }, [outletData])
  const outletGroupId = useMemo(() => getParameter("OUTLET_GROUP_ID") ?? null, [])

  // Header
  const [outletId, setOutletId] = useState(currentBranch?.id ?? null)
  const [outletName, setOutletName] = useState(currentBranch?.name ?? null)
  const [orderDate, setOrderDate] = useState(todayISO())
  const [notes, setNotes] = useState("")
  // ATALLA-467: diskon order-level bisa nominal (Rp) atau persen (%).
  const [discountMode, setDiscountMode] = useState("amount") // "amount" | "percent"
  const [orderDiscount, setOrderDiscount] = useState("")

  // Customer
  const [customer, setCustomer] = useState(null) // { id, name, phone, address }
  // 200726: alamat pengiriman — prefill dari alamat utama customer (editable).
  const [shippingAddress, setShippingAddress] = useState("")
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false)
  // 240726 (#6): modal "Pilih Banyak" produk (reuse ProductPickerModal pembelian).
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false)

  // Items: [{ product_unit_id, product_label, uom, qty, price, current_stock }]
  const [items, setItems] = useState([])
  // 290726 (permintaan user): satuan GLOBAL untuk seluruh order (pola sama dgn
  // menu Tambah Pembelian) — bukan per baris. Default ikut ENTITAS user:
  // VISION→PASANG, ATALLA→LUSIN; turun ke PCS bila satuan itu tak ada di master.
  // 290726: default DIISI SINKRON saat mount (bukan menunggu fetch master) supaya
  // dropdown langsung menampilkan satuan entitas sejak render pertama. Sebelum master
  // termuat nilainya label legacy (LUSIN) yang faktor fallback-nya benar; begitu
  // master datang, dinormalkan ke kode master (LSN) tanpa menimpa pilihan manual.
  const [globalUom, setGlobalUom] = useState(() => defaultUomForEntity(getCurrentEntity(), null))
  const [satuanOptions, setSatuanOptions] = useState(null)

  useEffect(() => {
    let cancelled = false
    loadMasterUomFactors().then((names) => {
      if (cancelled) return
      const opts = names.length ? names : [...UOM_OPTIONS]
      setSatuanOptions(opts)
      setGlobalUom((prev) => normalizeUomCode(prev) || prev)
    })
    return () => { cancelled = true }
  }, [])

  // Errors + submit state
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)

  // ── Product picker (ps_list) ──────────────────────────────────────────────
  const [selectedProductUnitId, setSelectedProductUnitId] = useState("")
  const [addQty, setAddQty] = useState("")
  const [productSelectKey, setProductSelectKey] = useState(0)
  const [productSearch, setProductSearch] = useState("")
  const [productSearchValue, setProductSearchValue] = useState("")
  const [productPage, setProductPage] = useState(1)
  const perPage = 20
  const productCacheRef = useRef(new Map())

  const debouncedSetProductSearch = useMemo(
    () =>
      _.debounce((val) => {
        setProductPage(1)
        setProductSearch(val)
      }, 400),
    []
  )
  useEffect(() => () => debouncedSetProductSearch.cancel(), [debouncedSetProductSearch])

  const onProductSearchChange = (val) => {
    setProductSearchValue(val)
    debouncedSetProductSearch(val)
  }

  // Reset pencarian saat outlet ganti.
  useEffect(() => {
    debouncedSetProductSearch.cancel()
    setSelectedProductUnitId("")
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)
    productCacheRef.current = new Map()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outletId])

  // ATALLA-472: harga & stok item terikat outlet asalnya. Kalau outlet diganti
  // SETELAH ada item, kosongkan item (dengan penjelasan) supaya harga/stok
  // selalu di-query ulang dari outlet baru — bukan pakai angka outlet lama.
  const itemsRef = useRef(items)
  useEffect(() => {
    itemsRef.current = items
  }, [items])
  const prevOutletRef = useRef(outletId)
  useEffect(() => {
    if (prevOutletRef.current === outletId) return
    prevOutletRef.current = outletId
    // 210726 (revisi ATALLA-472, permintaan user): item TIDAK lagi dikosongkan saat
    // outlet berubah — daftar dipertahankan. Konsekuensi disadari: harga & batas stok
    // per baris masih milik outlet lama; guard stok sesungguhnya tetap di server
    // (createHoOrder menolak INSUFFICIENT_STOCK utk outlet baru).
    if (itemsRef.current.length > 0) {
      toast("Outlet berubah — item dipertahankan. Cek kembali harga & stok per item bila perlu.", {
        id: "outlet-change",
        icon: "ℹ️",
        duration: 6000,
      })
    }
  }, [outletId])

  // 230726: prefill mode edit dari GET /orders/{id}. prevOutletRef ikut di-set
  // supaya effect "outlet berubah" tidak menampilkan toast palsu saat prefill.
  useEffect(() => {
    if (!isEdit) return
    let mounted = true
    ;(async () => {
      try {
        nProgress.start()
        const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
        const res = await axios.get(`${base}/orders/${editId}`)
        const o = res?.data || {}
        if (!mounted) return

        if (String(o.payment_status || "").toUpperCase() !== "REJECTED") {
          toast.error("Hanya order REJECTED yang bisa diedit")
          navigate("/ho-order", { state: { transition: "fade" } })
          return
        }

        prevOutletRef.current = o.outlet_id ?? null
        setOutletId(o.outlet_id ?? null)
        const b = (getBranchList() || []).find((x) => String(x?.id) === String(o.outlet_id))
        if (b?.name) setOutletName(b.name)

        setOrderDate(String(o.order_date || "").slice(0, 10) || todayISO())
        setNotes(o.order_notes || "")
        if (num(o.additional_discount_amount) > 0) {
          setDiscountMode("amount")
          setOrderDiscount(num(o.additional_discount_amount))
        }
        setCustomer((o.customer || o.customer_id)
          ? { id: o.customer_id ?? null, name: o.customer ?? "", phone: o.customer_phone ?? "", address: o.shipping_order?.full_address ?? "" }
          : null)
        setShippingAddress(o.shipping_order?.full_address ?? "")

        setItems((Array.isArray(o.items) ? o.items : [])
          .filter((r) => !r.source_order_id && String(r.record_status || "N").toUpperCase() !== "D")
          .map((r) => {
            const label = String(r.product_unit || "")
            const m = /\(([^()]+)\)\s*$/.exec(label)
            // 290726: label lama dari suffix "(LUSIN)" dipetakan ke kode master.
            const uom = normalizeUomCode(m ? m[1] : "PCS") || "PCS"
            const qty = num(r.quantity)
            // Preview diskon produk direkonstruksi dari total_discount tersimpan
            // (nominal per unit, disimpan per PCS) — server tetap otoritatif
            // menghitung ulang diskon saat simpan (prepareHoOrderData).
            const perUnitCut = qty > 0 ? num(r.total_discount) / qty : 0
            return {
              product_unit_id: String(r.product_unit_id),
              product_label: label.replace(/\s*\([^()]+\)\s*$/, "") || String(r.product_unit_id),
              uom,
              qty,
              price: num(r.price),
              dozen_store_price: 0,
              current_stock: null, // batas stok dijaga server saat simpan/approve
              discount_percentage: 0,
              max_discount_amount: perUnitCut / uomFactor(uom),
              discount_type: null,
            }
          }))
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Gagal memuat order")
      } finally {
        nProgress.done()
      }
    })()
    return () => { mounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId])

  const psListParams = useMemo(() => {
    if (!outletId) return null
    return {
      outlet_group_id: outletGroupId,
      outlet_id: outletId,
      only_in_stock: true,
      // 290726 (permintaan user): diskon produk HANYA basis store_price
      // (['store_price','both']) — harga order HO memang dari store_price, jadi
      // diskon ber-apply_to 'price' tidak ikut. Tanpa `discount_scope` ps_list
      // memakai default basis POS, yang persis itu.
      paginate: true,
      page: productPage,
      per_page: perPage,
      search: productSearch,
    }
  }, [outletId, outletGroupId, productSearch, productPage])

  const rsBaseUrl = psListParams ? process.env.REACT_APP_INVENTORY_API_URL : null
  const { data: productUnitSelection, isLoading: isLoadingProducts } = useResultSet(
    rsBaseUrl,
    "ps_list",
    psListParams || {}
  )
  const productUnitRows = useMemo(
    () => (Array.isArray(productUnitSelection?.data) ? productUnitSelection.data : []),
    [productUnitSelection]
  )
  useEffect(() => {
    for (const p of productUnitRows) {
      const key = String(p?.product_unit_id ?? "")
      if (key) productCacheRef.current.set(key, p)
    }
  }, [productUnitRows])

  const addItem = async () => {
    const pid = String(selectedProductUnitId ?? "")
    if (!pid) {
      toast.error("Pilih produk dulu")
      return
    }
    const p =
      productUnitRows.find((x) => String(x?.product_unit_id) === pid) ||
      productCacheRef.current.get(pid)
    if (!p) {
      toast.error("Produk tidak ditemukan (daftar berubah). Pilih ulang ya.")
      return
    }

    const qty = num(addQty || 1)
    if (qty <= 0) {
      toast.error("Qty harus lebih dari 0")
      return
    }

    // ATALLA-471: qty tidak boleh melebihi stok tersedia di outlet ini.
    const available = p?.current_stock != null ? num(p.current_stock) : null

    // 210726: outlet terpilih tanpa harga -> fallback harga HO Atalla (outlet pusat).
    let itemPrice = pickSellPrice(p)
    let itemDozen = num(p?.dozen_store_price ?? 0)
    if (itemPrice <= 0) {
      const hq = await fetchHqFallbackPrice(pid)
      if (hq && hq.price > 0) {
        itemPrice = hq.price
        if (itemDozen <= 0) itemDozen = hq.dozen
        toast("Harga outlet ini kosong — memakai harga HO Atalla", { icon: "ℹ️" })
      }
    }

    const existsIndex = items.findIndex((x) => String(x.product_unit_id) === pid)
    if (existsIndex >= 0) {
      const nextQty = num(items[existsIndex].qty) + qty
      if (available != null && nextQty > available) {
        toast.error(`Qty total (${nextQty}) melebihi stok tersedia (${available})`)
        return
      }
      const next = [...items]
      next[existsIndex] = { ...next[existsIndex], qty: nextQty }
      setItems(next)
      toast.success("Qty item ditambahkan")
    } else {
      if (available != null && qty > available) {
        toast.error(`Qty (${qty}) melebihi stok tersedia (${available})`)
        return
      }
      setItems([
        {
          product_unit_id: pid,
          product_label: pickProductLabel(p),
          uom: globalUom,
          qty,
          // 290726: harga mengikuti satuan terpilih — LUSIN pakai dozen_store_price
          // bila tersedia, selain itu harga per pcs dikali faktor satuan.
          price: isDozenUom(globalUom) && num(itemDozen) > 0
            ? num(itemDozen)
            : num(itemPrice) * uomFactor(globalUom),
          base_pcs_price: num(itemPrice),
          // 210726: harga lusin outlet — dipakai saat satuan LUSIN dipilih
          // (dozen_store_price dulu, fallback harga pcs × 12).
          dozen_store_price: itemDozen,
          current_stock: available,
          // Diskon produk aktif (dari ps_list) — dipakai utk PREVIEW; server yang
          // menghitung final di createHoOrder (otoritatif).
          discount_percentage: num(p?.discount_percentage),
          max_discount_amount: num(p?.max_discount_amount),
          discount_type: p?.discount_type || null,
        },
        ...items,
      ])
      toast.success("Item ditambahkan")
    }

    debouncedSetProductSearch.cancel()
    setSelectedProductUnitId("")
    setAddQty("")
    setProductSearchValue("")
    setProductSearch("")
    setProductPage(1)
    setProductSelectKey((k) => k + 1)
  }

  // 240726 (#6): tambah banyak produk sekaligus dari ProductPickerModal — merge
  // per unit (unit sudah ada → qty dijumlah, clamp ke stok tersedia). Harga/diskon
  // dari ps_list; server tetap otoritatif saat createHoOrder.
  const onAddItemsBulk = (entries) => {
    if (!entries?.length) return
    setItems((prev) => {
      let next = [...prev]
      for (const { product: p, qty } of entries) {
        const pid = String(p?.product_unit_id ?? "")
        if (!pid) continue
        productCacheRef.current.set(pid, p)
        const available = p?.current_stock != null ? num(p.current_stock) : null
        const q = num(qty) > 0 ? num(qty) : 1
        const idx = next.findIndex((x) => String(x.product_unit_id) === pid)
        if (idx >= 0) {
          const nextQty = available != null ? Math.min(num(next[idx].qty) + q, available) : num(next[idx].qty) + q
          next[idx] = { ...next[idx], qty: nextQty }
        } else {
          next = [
            {
              product_unit_id: pid,
              product_label: pickProductLabel(p),
              // 290726: ikut satuan GLOBAL yang dipilih di depan (bukan selalu PCS),
              // dan harga dikonversi ke satuan itu — sama seperti tambah item satuan.
              uom: globalUom,
              qty: available != null ? Math.min(q, available) : q,
              price: isDozenUom(globalUom) && num(p?.dozen_store_price) > 0
                ? num(p.dozen_store_price)
                : num(pickSellPrice(p)) * uomFactor(globalUom),
              base_pcs_price: num(pickSellPrice(p)),
              dozen_store_price: num(p?.dozen_store_price ?? 0),
              current_stock: available,
              discount_percentage: num(p?.discount_percentage),
              max_discount_amount: num(p?.max_discount_amount),
              discount_type: p?.discount_type || null,
            },
            ...next,
          ]
        }
      }
      return next
    })
    toast.success(`${entries.length} item ditambahkan`)
    setBulkPickerOpen(false)
  }

  const updateItem = (index, patch) => {
    const next = [...items]
    next[index] = { ...next[index], ...patch }
    setItems(next)
  }

  // ATALLA-471: blok input qty di atas stok tersedia — clamp ke stok + kabari user.
  const updateItemQty = (index, rawValue) => {
    const it = items[index]
    if (!it) return
    const available = it.current_stock != null ? num(it.current_stock) : null
    const f = uomFactor(it.uom)
    const qty = num(rawValue)
    // 200726 (PCS/LUSIN): stok dalam PCS — qty dikonversi & DIBULATKAN
    // (210726: hindari pembanding pecahan 5,04 vs 5 dari lusin desimal).
    if (available != null && Math.round(qty * f) > available) {
      const maxQty = Math.floor(available / f)
      toast.error(`Stok tersedia hanya ${available} pcs (maks ${maxQty} ${it.uom || "PCS"})`, { id: `qty-${it.product_unit_id}` })
      updateItem(index, { qty: maxQty })
      return
    }
    updateItem(index, { qty: rawValue })
  }
  const removeItem = (index) => setItems(items.filter((_, i) => i !== index))

  // ── Totals (pratinjau; PPN inclusive) ─────────────────────────────────────
  // ATALLA-467: mode "percent" → nominal diskon = subtotal × pct/100 (server
  // menghitung ulang dengan rumus yang sama; nilai FE hanya pratinjau).
  const totals = useMemo(() => {
    const subtotal = items.reduce((acc, it) => acc + num(it.qty) * num(it.price), 0)
    const productDiscount = items.reduce((acc, it) => acc + lineProductDiscount(it), 0)
    // Basis diskon order = subtotal SETELAH diskon produk (mirror server).
    const afterProduct = Math.max(0, subtotal - productDiscount)
    const discount =
      discountMode === "percent"
        ? Math.min((afterProduct * Math.min(num(orderDiscount), 100)) / 100, afterProduct)
        : Math.min(num(orderDiscount), afterProduct)
    const nett = Math.max(0, afterProduct - discount)
    // 210726: order HO ke customer tanpa DPP/PPN.
    // 220726 (permintaan user): Total Qty via PCS riil — tiap baris dibulatkan ke
    // pcs utuh (0,17 lusin ×12 = 2,04 → 2 pcs), dijumlah, dibagi balik faktor
    // satuan; campur satuan → tampil pcs. Paritas rumus form PO + dokumen backend
    // (menjumlah desimal lusin langsung menumpuk error: 6 × 0,17 = 1,02 ≠ 1 lusin).
    const totalPcs = items.reduce((acc, it) => acc + Math.round(num(it.qty) * uomFactor(it.uom)), 0)
    const factors = [...new Set(items.map((it) => uomFactor(it.uom)))]
    const displayFactor = factors.length === 1 ? factors[0] : 1
    const qtyTotal = Math.round((totalPcs / displayFactor) * 100) / 100
    return { subtotal, productDiscount, discount, nett, qtyTotal }
  }, [items, orderDiscount, discountMode])

  const handleSubmit = () => {
    if (submittingRef.current) return

    const nextErrors = {}
    if (!outletId) nextErrors.outlet_id = "Outlet wajib dipilih"
    // ATALLA-469: customer wajib dipilih sebelum simpan.
    if (!customer?.name && !customer?.id) nextErrors.customer = "Customer wajib dipilih"
    if (items.length === 0) nextErrors.items = "Minimal 1 item"
    if (items.some((it) => num(it.qty) <= 0)) nextErrors.items = "Qty setiap item harus > 0"
    // ATALLA-471: tolak submit bila ada qty melebihi stok tersedia.
    const overStock = items.find(
      (it) => it.current_stock != null && num(it.qty) > num(it.current_stock)
    )
    if (overStock) {
      nextErrors.items = `Qty "${overStock.product_label}" melebihi stok tersedia (${num(overStock.current_stock)})`
    }
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      toast.error(Object.values(nextErrors)[0])
      return
    }

    const payload = {
      outlet_id: outletId,
      customer_id: customer?.id ?? null,
      customer: customer?.name ?? null,
      customer_phone: customer?.phone ?? null,
      shipping_address: shippingAddress.trim() || null,
      // ATALLA-467: kirim persen ATAU nominal — server yang menghitung nominal
      // final dari subtotal ketika persen dipakai.
      order_discount_amount: discountMode === "percent" ? null : num(orderDiscount),
      order_discount_percent: discountMode === "percent" ? Math.min(num(orderDiscount), 100) : null,
      order_notes: notes || null,
      order_date: orderDate,
      items: items.map((it) => ({
        product_unit_id: it.product_unit_id,
        quantity: num(it.qty),
        price: num(it.price),
        uom: it.uom || "PCS",
      })),
    }

    const onSuccess = (result, message) => {
      submittingRef.current = false
      setSubmitting(false)
      nProgress.done()
      toast.success(message || (isEdit ? "Order HO diperbarui" : "Order HO berhasil dibuat"), { id: "submit" })
      // ATALLA-474: setelah order dibuat, arahkan ke daftar order HO —
      // cetak DO/Faktur & pembatalan tersedia dari baris daftar.
      navigate("/ho-order", { state: { transition: "fade" } })
    }
    const onError = (message, validationErrors) => {
      submittingRef.current = false
      setSubmitting(false)
      nProgress.done()
      if (validationErrors && typeof validationErrors === "object") setErrors(validationErrors)
      toast.error(message || "Gagal menyimpan order HO", { id: "submit" })
    }

    submittingRef.current = true
    setSubmitting(true)
    nProgress.start()
    toast.loading("Menyimpan...", { id: "submit" })

    // 230726: mode edit (order REJECTED) → update-ho; selain itu create biasa.
    if (isEdit) {
      ;(async () => {
        try {
          const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
          const res = await axios.post(`${base}/orders/${editId}/update-ho`, { ...payload, created_by: user?.name })
          onSuccess(res?.data, res?.data?.message)
        } catch (e) {
          const json = e?.response?.data
          const flat = json?.errors && typeof json.errors === "object"
            ? Object.values(json.errors).flat().filter(Boolean)
            : []
          onError(flat.length > 0 ? String(flat[0]) : (json?.message || e.message), json?.errors)
        }
      })()
      return
    }

    insertHoOrder(payload, user?.name, onSuccess, onError)
  }

  const productPlaceholder = !outletId ? "-- Pilih Outlet Dulu --" : "-- Cari & Pilih Produk --"

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-24">
      {/* Header */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-neutral-700">Outlet (HO)</label>
            {canPickOutlet ? (
              <SelectSearchable
                selection={outletOptions}
                value={outletId}
                onChange={(value, row) => {
                  setOutletId(value)
                  setOutletName(row?.name ?? null)
                }}
                keyValue={(v) => v?.id}
                title={(v) => v?.name || "-"}
                placeholder="-- Pilih Outlet --"
              />
            ) : (
              <Input disabled value={outletName || currentBranch?.name || ""} />
            )}
            <ErrorMessage error={errors.outlet_id} />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Tanggal Order</label>
            <input
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2 mt-1">
              <div className={`flex-1 px-4 py-2 text-sm border rounded-xl bg-neutral-50 ${errors.customer && !customer ? "border-red-300" : "border-neutral-200"}`}>
                {customer ? (
                  <span>
                    {customer.name} {customer.phone ? `• ${customer.phone}` : ""}
                  </span>
                ) : (
                  <span className="text-neutral-400">Wajib pilih customer</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setCustomerPickerOpen(true)}
                className="px-3 py-2 text-sm text-white bg-brand-800 rounded-xl hover:bg-brand-900"
              >
                Pilih
              </button>
              {customer && (
                <button
                  type="button"
                  onClick={() => { setCustomer(null); setShippingAddress("") }}
                  className="px-3 py-2 text-sm text-neutral-600 border border-neutral-200 rounded-xl"
                >
                  Hapus
                </button>
              )}
            </div>
            <ErrorMessage error={!customer ? errors.customer : null} />
          </div>

          {/* 200726: alamat pengiriman — otomatis terisi dari alamat utama customer, bisa diedit. */}
          <div>
            <label className="block text-xs text-neutral-700">Alamat Pengiriman</label>
            <textarea
              rows={3}
              value={shippingAddress}
              onChange={(e) => setShippingAddress(e.target.value)}
              placeholder="Terisi otomatis dari alamat customer — bisa diubah. Kosongkan bila tidak dikirim."
              className="w-full px-3 py-2 mt-1 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400"
            />
          </div>

          <div>
            <label className="block text-xs text-neutral-700">Catatan</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Catatan order (opsional)"
              className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold text-neutral-900">Items</div>
            <div className="text-xs text-neutral-500">
              Total Qty: <b className="text-orange-500">{qtyFmt(totals.qtyTotal)}</b>
            </div>
          </div>
          <ErrorMessage error={errors.items} />

          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-6">
              <label className="block text-xs text-neutral-700">Produk</label>
              <SelectSearchable
                key={`ho-product-select-${productSelectKey}`}
                disabled={!outletId}
                selection={productUnitRows}
                isLoading={isLoadingProducts}
                value={selectedProductUnitId}
                onChange={(value) => setSelectedProductUnitId(String(value ?? ""))}
                keyValue={(v) => String(v?.product_unit_id ?? "")}
                title={(v) =>
                  v?.product_unit_long_name || v?.product_unit || v?.sku || v?.product_model || v?.product_unit_id || "-"
                }
                description={(v) => {
                  const parts = [
                    v?.product_model,
                    v?.sku ? `SKU: ${v.sku}` : null,
                    v?.product_brand,
                    v?.current_stock != null ? `Stok: ${v.current_stock}` : null,
                    v?.price != null ? `Harga: ${moneyFmt(v.price)}` : null,
                  ].filter(Boolean)
                  return parts.join(" • ")
                }}
                placeholder={productPlaceholder}
                reverse
                serverSearch
                searchValue={productSearchValue}
                onSearchChange={onProductSearchChange}
                page={productPage}
                onNextPage={() => {
                  if (productUnitSelection?.meta?.next_page_url) setProductPage((p) => p + 1)
                }}
                onPrevPage={() => {
                  if (productUnitSelection?.meta?.prev_page_url) setProductPage((p) => Math.max(1, p - 1))
                }}
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs text-neutral-700">Qty</label>
              <input
                type="number"
                min={1}
                value={addQty}
                onChange={(e) => setAddQty(e.target.value)}
                className="block w-full px-4 py-2 mt-1 text-sm transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={addItem}
                disabled={!outletId}
                className="w-full px-4 py-2 mt-1 text-sm text-white bg-brand-800 rounded-xl hover:bg-brand-900 disabled:bg-neutral-300"
              >
                Tambah
              </button>
            </div>
            {/* 240726 (#6): pilih banyak produk sekaligus (mirror menu pembelian). */}
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => {
                  if (!outletId) return toast.error("Pilih outlet dulu")
                  setBulkPickerOpen(true)
                }}
                disabled={!outletId}
                className="w-full px-4 py-2 mt-1 text-sm font-semibold border rounded-xl border-brand-900 text-neutral-900 hover:bg-neutral-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Pilih Banyak
              </button>
            </div>
          </div>

          {/* Mount kondisional: hook fetch modal hanya hidup saat terbuka. */}
          {bulkPickerOpen && (
            <ProductPickerModal
              open
              onClose={() => setBulkPickerOpen(false)}
              onAdd={onAddItemsBulk}
              outletId={outletId}
              outletGroupId={outletGroupId}
              uom={globalUom}
            />
          )}

          {/* 290726 (permintaan user): satuan GLOBAL seluruh item — pola & rumus
              konversi sama dengan menu Tambah Pembelian. Default ikut entitas user. */}
          <div className="flex items-center gap-2 mb-3">
            <label className="text-xs font-medium text-neutral-600">Satuan (semua item)</label>
            <select
              value={globalUom}
              onChange={(e) => {
                const nextUom = e.target.value
                setItems((prev) =>
                  prev.map((it) => {
                    // 290726 (laporan user): konversi SELALU dari harga DASAR per PCS,
                    // bukan dari harga satuan sebelumnya. Kalau diturunkan dari harga
                    // satuan lama, LUSIN -> PCS menghasilkan dozen_store_price / 12 dan
                    // bukan harga pcs aslinya (dozen bukan selalu kelipatan 12).
                    const prevF = uomFactor(it.uom)
                    const nextF = uomFactor(nextUom)
                    const basePcs = num(it.base_pcs_price) > 0
                      ? num(it.base_pcs_price)
                      : num(it.price) / prevF
                    let nextPrice = basePcs * nextF
                    if (isDozenUom(nextUom) && num(it.dozen_store_price) > 0) {
                      nextPrice = num(it.dozen_store_price)
                    }
                    return { ...it, uom: nextUom, price: nextPrice, base_pcs_price: basePcs }
                  })
                )
                setGlobalUom(nextUom)
              }}
              className="w-32 px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring focus:ring-neutral-200"
            >
              {/* Satuan order lama yang tak ada di master tetap diinjeksi supaya
                  value select tidak kosong. */}
              {((satuanOptions ?? UOM_OPTIONS).includes(globalUom)
                ? (satuanOptions ?? UOM_OPTIONS)
                : [globalUom, ...(satuanOptions ?? UOM_OPTIONS)]
              ).map((u) => (
                <option key={u} value={u}>{u}</option>
              ))}
            </select>
          </div>

          {/* Item list */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500 border-b">
                  <th className="py-2">Produk</th>
                  <th className="py-2 w-24">Qty</th>
                  <th className="py-2 w-40">Harga</th>
                  <th className="py-2 w-40 text-right">Subtotal</th>
                  <th className="py-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-neutral-400">
                      Belum ada item
                    </td>
                  </tr>
                )}
                {items.map((it, index) => (
                  <tr key={it.product_unit_id} className="border-b">
                    <td className="py-2 pr-2">
                      <div className="font-medium text-neutral-800">{it.product_label}</div>
                      <div className="text-xs text-neutral-400">
                        {it.product_unit_id}
                        {it.current_stock != null ? ` • Stok: ${it.current_stock}` : ""}
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={1}
                          max={it.current_stock != null ? Math.floor(num(it.current_stock) / uomFactor(it.uom)) : undefined}
                          value={it.qty}
                          onChange={(e) => updateItemQty(index, e.target.value)}
                          onBlur={(e) => updateItemQty(index, qtyInputStr(e.target.value))}
                          className="w-20 px-2 py-1 text-sm border border-neutral-200 rounded-lg"
                        />
                        {/* 290726 (permintaan user): dropdown satuan PER ITEM dihapus —
                            satuan kini SATU untuk seluruh order (dropdown global di atas
                            daftar item). Dropdown lama ini juga sumber bug "default LSN
                            tidak ke-apply": daftar opsinya masih PCS/LUSIN/PASANG lama,
                            sehingga item ber-satuan LSN (kode master) dipantulkan tampil
                            "PCS". Satuan baris ditampilkan sebagai teks saja. */}
                        <span className="px-1 py-1 text-xs font-medium text-neutral-500">
                          {normalizeUomCode(it.uom) || "PCS"}
                        </span>
                      </div>
                      {it.current_stock != null && (
                        <div className="text-[10px] text-neutral-400">
                          maks {Math.floor(num(it.current_stock) / uomFactor(it.uom))} {String(it.uom || "PCS").toUpperCase()}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      <input
                        type="number"
                        min={0}
                        value={it.price}
                        onChange={(e) => updateItem(index, { price: e.target.value })}
                        className="w-36 px-2 py-1 text-sm border border-neutral-200 rounded-lg"
                      />
                    </td>
                    <td className="py-2 text-right">
                      {moneyFmt(num(it.qty) * num(it.price))}
                      {lineProductDiscount(it) > 0 && (
                        <div className="text-[11px] text-rose-600">
                          Diskon{num(it.discount_percentage) > 0 ? ` ${num(it.discount_percentage)}%` : ""}: - {moneyFmt(lineProductDiscount(it))}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Totals */}
      <div className="border border-neutral-200 rounded-xl">
        <div className="px-4 py-5 sm:p-6 space-y-3">
          <div className="flex justify-between items-center">
            <label className="text-sm text-neutral-700">Diskon Order</label>
            <div className="flex items-center gap-2">
              {/* ATALLA-467: toggle Rp | % */}
              <div className="inline-flex border border-neutral-200 rounded-xl overflow-hidden">
                {[
                  { key: "amount", label: "Rp" },
                  { key: "percent", label: "%" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => {
                      if (discountMode !== opt.key) {
                        setDiscountMode(opt.key)
                        setOrderDiscount("")
                      }
                    }}
                    className={`px-3 py-2 text-sm font-medium ${
                      discountMode === opt.key
                        ? "bg-brand-800 text-white"
                        : "bg-white text-neutral-600 hover:bg-neutral-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                type="number"
                min={0}
                max={discountMode === "percent" ? 100 : undefined}
                step={discountMode === "percent" ? 0.01 : 1}
                value={orderDiscount}
                onChange={(e) => setOrderDiscount(e.target.value)}
                placeholder="0"
                className="w-40 px-3 py-2 text-sm text-right border border-neutral-200 rounded-xl"
              />
            </div>
          </div>
          {discountMode === "percent" && (
            <div className="text-xs text-right text-neutral-500">
              {Math.min(num(orderDiscount), 100)}% dari subtotal = {moneyFmt(totals.discount)}
            </div>
          )}
          <div className="border-t pt-3 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-500">Subtotal</span>
              <span>{moneyFmt(totals.subtotal)}</span>
            </div>
            {totals.productDiscount > 0 && (
              <div className="flex justify-between">
                <span className="text-neutral-500">Diskon Produk</span>
                <span className="text-rose-600">- {moneyFmt(totals.productDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-500">Diskon Order</span>
              <span>- {moneyFmt(totals.discount)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold pt-1 border-t">
              <span>Total (Nett)</span>
              <span className="text-orange-500">{moneyFmt(totals.nett)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate("/ho-order")}
          className="px-5 py-2.5 text-sm text-neutral-700 border border-neutral-200 rounded-xl"
        >
          Batal
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || items.length === 0 || !customer}
          title={!customer ? "Pilih customer dulu" : undefined}
          className="px-6 py-2.5 text-sm font-medium text-white bg-brand-500 rounded-xl hover:bg-brand-600 disabled:bg-neutral-300"
        >
          {submitting ? "Menyimpan..." : "Simpan Order"}
        </button>
      </div>

      <CustomerPickerModal
        open={customerPickerOpen}
        onClose={() => setCustomerPickerOpen(false)}
        onSelect={(c) => {
          setCustomer({ id: c?.id ?? null, name: c?.name ?? "", phone: c?.phone ?? "", address: c?.address ?? "" })
          // 200726: prefill alamat pengiriman dari alamat utama customer (tetap editable).
          setShippingAddress(c?.address ?? "")

          // 270726 (permintaan user): diskon default customer (Master Customer HO)
          // dipakai sebagai nilai awal diskon order — tetap bisa diubah manual.
          const defaultDiscount = Number(c?.default_discount_percent ?? 0)
          if (defaultDiscount > 0) {
            setDiscountMode("percent")
            setOrderDiscount(defaultDiscount)
            toast(`Diskon default customer ${defaultDiscount}% diterapkan`, { icon: "🏷️" })
          }

          setCustomerPickerOpen(false)
        }}
      />
    </div>
  )
}
